import React, { useState } from "react";
import { Menu as MenuIcon, X, Instagram, Facebook, MessageCircle, ShoppingBag } from "lucide-react";
import { useDialogs } from "./DialogProvider";
import { scrollToId, scrollToTop } from "@/lib/scroll";
import { CONTACT } from "@/data/cafe";

const SOCIALS = [
  { Icon: Instagram, href: CONTACT.socials.instagram, label: "instagram" },
  { Icon: Facebook, href: CONTACT.socials.facebook, label: "facebook" },
  { Icon: MessageCircle, href: CONTACT.socials.whatsapp, label: "whatsapp" },
];

const LINKS = [
  { label: "About", href: "#about" },
  { label: "Menu", href: "#menu" },
  { label: "Signature", href: "#signature" },
  { label: "Gallery", href: "#gallery" },
  { label: "Locations", href: "#locations" },
  { label: "Spaces", href: "#spaces" },
  { label: "Offers", href: "#offers" },
  { label: "Events", href: "#events" },
  { label: "Franchise", href: "#franchise" },
  { label: "Contact", href: "#contact" },
];

export default function Header() {
  const [openMenu, setOpenMenu] = useState(false);
  const { open, cartCount } = useDialogs();

  const go = (href) => { setOpenMenu(false); scrollToId(href); };

  return (
    <>
      {/* Slim top bar: logo + order + mobile toggle */}
      <header data-testid="site-header" className="fixed top-0 inset-x-0 z-50">
        <div className="max-w-7xl mx-auto px-5 md:px-8 mt-3">
          <div className="flex items-center justify-between h-[60px] px-4 rounded-full bg-[#F5F2EB]/85 backdrop-blur-xl border border-[#1A1514]/10 shadow-[0_8px_30px_rgba(0,0,0,0.06)]">
            <button onClick={scrollToTop} className="flex items-center gap-2.5 group shrink-0" data-testid="logo-btn">
              <img src="/logo.png" alt="Teaavi Coffee" className="h-11 w-11 rounded-full object-cover ring-2 ring-[#1C7C54]/60 shadow-sm bg-white" />
              <span className="font-display text-lg sm:text-xl tracking-tight text-[#1A1514] whitespace-nowrap">Teaavi <span className="text-[#1C7C54]">Coffee</span></span>
            </button>

            <div className="flex items-center gap-2">
              <div className="hidden lg:flex items-center gap-1 mr-1" data-testid="header-socials">
                {SOCIALS.map(({ Icon, href, label }) => (
                  <a key={label} href={href} target="_blank" rel="noreferrer" aria-label={label} data-testid={`header-social-${label}`}
                    className="h-9 w-9 grid place-items-center rounded-full text-[#1A1514]/70 hover:text-[#F5F2EB] hover:bg-[#1C7C54] transition-colors">
                    <Icon className="h-4 w-4" />
                  </a>
                ))}
              </div>
              <button onClick={() => open("order")} data-testid="header-cart-btn" aria-label="View cart"
                className="relative h-9 w-9 grid place-items-center rounded-full text-[#1A1514] hover:text-[#F5F2EB] hover:bg-[#1C7C54] transition-colors">
                <ShoppingBag className="h-[18px] w-[18px]" />
                {cartCount > 0 && (
                  <span data-testid="header-cart-count"
                    className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 grid place-items-center rounded-full bg-[#1C7C54] text-[#F5F2EB] font-accent text-[10px] leading-none">
                    {cartCount}
                  </span>
                )}
              </button>
              <button onClick={() => open("order")} data-testid="header-order-btn"
                className="hidden sm:inline-flex rounded-full bg-[#1C7C54] text-[#F5F2EB] px-5 py-2.5 font-accent text-xs uppercase tracking-[0.15em] hover:bg-[#14603F] transition-colors">
                Order Online
              </button>
              <button className="lg:hidden text-[#1A1514] p-1" onClick={() => setOpenMenu((v) => !v)} data-testid="mobile-menu-toggle">
                {openMenu ? <X className="h-6 w-6" /> : <MenuIcon className="h-6 w-6" />}
              </button>
            </div>
          </div>
        </div>

        {openMenu && (
          <div className="lg:hidden mx-5 mt-2 rounded-2xl bg-[#1A1514] border border-white/10 px-5 py-4" data-testid="mobile-menu">
            <div className="flex flex-col gap-1">
              {LINKS.map((l) => (
                <button key={l.href} onClick={() => go(l.href)} data-testid={`mnav-${l.label.toLowerCase()}`}
                  className="text-left font-accent text-sm uppercase tracking-[0.15em] text-[#F5F2EB]/80 py-2.5 hover:text-[#1C7C54] transition-colors">
                  {l.label}
                </button>
              ))}
              <button onClick={() => { setOpenMenu(false); open("order"); }}
                className="mt-2 rounded-full bg-[#1C7C54] text-[#F5F2EB] px-5 py-3 font-accent text-xs uppercase tracking-[0.15em]">
                Order Online
              </button>
              <div className="flex items-center gap-2 mt-3 pt-3 border-t border-white/10">
                {SOCIALS.map(({ Icon, href, label }) => (
                  <a key={label} href={href} target="_blank" rel="noreferrer" aria-label={label} data-testid={`mobile-social-${label}`}
                    className="h-10 w-10 grid place-items-center rounded-full border border-white/20 text-[#F5F2EB] hover:bg-[#1C7C54] hover:border-[#1C7C54] transition-colors">
                    <Icon className="h-4 w-4" />
                  </a>
                ))}
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Floating bottom navigation (desktop) */}
      <nav data-testid="bottom-nav"
        className="hidden lg:flex fixed bottom-5 left-1/2 -translate-x-1/2 z-50 items-center gap-1 px-2.5 py-2 rounded-full bg-[#1A1514]/80 backdrop-blur-xl border border-white/12 shadow-[0_8px_30px_rgba(0,0,0,0.25)]">
        {LINKS.map((l) => (
          <button key={l.href} onClick={() => go(l.href)} data-testid={`nav-${l.label.toLowerCase()}`}
            className="rounded-full px-3.5 py-1.5 font-accent text-[11px] uppercase tracking-[0.14em] text-[#F5F2EB]/75 hover:text-[#F5F2EB] hover:bg-[#1C7C54] transition-colors">
            {l.label}
          </button>
        ))}
      </nav>
    </>
  );
}
